import axios from "axios";
import { NotImplementedError, agentSessionFromService } from "../..";
import { PromiseHandler } from "../../handlers";
import { parseIdFilter } from "../../handlers/filter";
import { AgentSessionIds } from "./sessionIds";
import { agentSlug } from "./agents.config";
import { AgentSessionsFetchFailedError, AgentSessionBadRequest, handleAgentSessionRequestError } from "./errors";
import { AgentSessionsLists } from "./sessions.lists";
import { AgentSessionEvents } from "./sessions.events";
import { AgentUploads } from "./sessions.uploads";
export * from "./errors";
/**
 * AgentSessions is a class that manages ai agent sessions,
 * allowing you to find sessions — using `find`,
 * and prompt a session using `save({ id, message })`.
 */
export class AgentSessions extends PromiseHandler {
    constructor(sdk, agent) {
        super(sdk, `ai.agents.${agent}`, { atomize: true });
        this.fromService = async (session) => {
            const events = await Promise.all((session.events || []).map((event) => this.events.fromService(event)));
            return { ...agentSessionFromService(session), events };
        };
        this.ids = new AgentSessionIds(sdk, agent);
        this.ensuredIds = new AgentSessionIds(sdk, agent, { ensuredSession: true });
        this.lists = new AgentSessionsLists(sdk, agent);
        this.events = new AgentSessionEvents(sdk, agent);
        this.uploads = new AgentUploads(sdk, agent);
        this.agentName = agent;
    }
    get agent() {
        return this.agentName;
    }
    /**
     * Finds agent sessions based on the provided filters — currently, only supports filtering by ID.
     * @throws {AgentSessionNotFoundError} If the session ID is not found.
     * @throws {AgentSessionsFetchFailedError} If there was an error fetching the session.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     * @returns the agent sessions matching the provided filters.
     */
    async findItems(filters) {
        const id = parseIdFilter(filters)?.values().next().value;
        if (!id) {
            return [];
        }
        return this.getSession(id).then((session) => (session ? session : []));
    }
    /**
     * Logic for fetching a single agent session by ID.
     * This method is used internally by `findItems`.
     */
    getSession(sessionId) {
        if (!sessionId) {
            throw new AgentSessionsFetchFailedError();
        }
        return this.cache.promise(sessionId, async () => {
            try {
                let serviceSession;
                if (this.sdk.ai.isLegacyEndpoint(this.agent)) {
                    serviceSession = await this.getSessionLegacy(sessionId);
                }
                else {
                    // ADK has no dedicated "get session" REST verb — it's a sync `class_method`
                    // dispatched through the same `:query` endpoint used for `create_session`.
                    const { data } = await this.sdk.ai.withIdTokenRetry(this.agent, () => axios.post(`${this.sdk.urls.baseAiAgentsUrl}/${agentSlug(this.agent)}/:query`, {
                        class_method: "async_get_session",
                        input: { user_id: this.sdk.getCurrentUser()?.id, session_id: sessionId }
                    }, this.sdk.ai.axiosRequestConfig(this.agent)));
                    serviceSession = data?.output;
                }
                if (!serviceSession) {
                    throw new AgentSessionsFetchFailedError(sessionId);
                }
                const agentSession = await this.fromService(serviceSession);
                return [this.events.attachments.hydrate(agentSession)];
            }
            catch (err) {
                handleAgentSessionRequestError(err, {
                    id: sessionId,
                    defaultError: new AgentSessionsFetchFailedError(sessionId)
                });
            }
        });
    }
    async getSessionLegacy(sessionId) {
        const res = await axios.get(`${this.sdk.urls.baseAiAgentsUrlLegacy}/${this.agent}/sessions/${sessionId}`, this.sdk.ai.axiosRequestConfig(this.agent));
        return res.data;
    }
    /**
     * Save is used to prompt an agent session with a message.
     * It will return the updated session with the new message and any response from the agent.
     * @throws {AgentSessionBadRequest} If the message or session ID is missing.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     * @throws {AgentSessionNotFoundError} If the session ID is not found (404).
     * @throws {AgentSessionsFetchFailedError} If there was an error fetching the session after prompting.
     */
    async saveItem({ message = "", id: sessionId }, { context, sse, signal } = {}) {
        if (!message && !context?.attachments?.length) {
            throw new AgentSessionBadRequest("Message or files are required to prompt an agent.");
        }
        if (!sessionId) {
            throw new AgentSessionBadRequest("Session ID is required to prompt an agent.");
        }
        const opts = { agent: this.agent, sessionId, message, context, signal };
        await this.sdk.ai.runSSE(opts, sse);
        const [session] = await this.getSession(sessionId);
        this.events.attachments.setOnLastUserMessage(message, session, context?.attachments);
        return this.events.attachments.hydrate(session);
    }
    /**
     * @deprecated Deleting agent sessions is not supported.
     */
    async deleteItem(_id) {
        throw new NotImplementedError("Deleting agent sessions is not supported.");
    }
}
//# sourceMappingURL=sessions.js.map